import React, { useState } from 'react';
import { submitContactForm } from '../../services/api';
import { renderRichText } from '../../utils/richTextRenderer';
import { IoClose, IoCheckmark } from 'react-icons/io5';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';

const ProductDetails = ({ product }) => {
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  if (!product) {
    return null;
  }



  const handleGetQuote = () => {
    setShowQuoteModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage('');
    
    try {
      // Prepare the data for API submission
      const contactData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: `Product Quote Request - ${product.Title}${formData.company ? ` (${formData.company})` : ''}`,
        message: `Product: ${product.Title}\n\n${formData.message || 'No additional details provided.'}`
      };

      // Submit to API
      await submitContactForm(contactData);
      
      setFormSubmitted(true);
      setSubmitStatus('success');
      
      // Reset form after 3 seconds and close modal
      setTimeout(() => {
        setShowQuoteModal(false);
        setFormSubmitted(false);
        setSubmitStatus(null);
        setErrorMessage('');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          message: ''
        });
      }, 3000);
    } catch (error) {
      console.error('Error submitting quote request:', error);
      setSubmitStatus('error');
      setErrorMessage(error.message || 'Failed to submit quote request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Main Details */}
          <div className="lg:col-span-2">
            <h2 className="text-3xl font-bold text-catalina-blue mb-6">
              Product Details
            </h2>
            
            {product.Description && (
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Description</h3>
                <div className="text-gray-600 leading-relaxed space-y-3">
                  {renderRichText(product.Description)}
                </div>
              </div>
            )}

            {product.Specifications && (
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Specifications</h3>
                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="prose prose-sm max-w-none text-gray-600">
                    {typeof product.Specifications === 'string' ? (
                      <p className="whitespace-pre-line">{product.Specifications}</p>
                    ) : Array.isArray(product.Specifications) ? (
                      <ul className="space-y-2">
                        {product.Specifications.map((spec, index) => (
                          <li key={index} className="flex items-start">
                            <span className="text-catalina-blue mr-2">•</span>
                            <span>{typeof spec === 'string' ? spec : JSON.stringify(spec)}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <dl className="space-y-3">
                        {Object.entries(product.Specifications).map(([key, value]) => (
                          <div key={key} className="flex flex-col sm:flex-row sm:gap-4">
                            <dt className="font-semibold text-gray-900 min-w-[200px]">{key}:</dt>
                            <dd className="text-gray-600">{String(value)}</dd>
                          </div>
                        ))}
                      </dl>
                    )}
                  </div>
                </div>
              </div>
            )}

            {product.Applications && (
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Applications</h3>
                <div className="bg-gray-50 rounded-lg p-6 text-gray-600 leading-relaxed space-y-3">
                  {renderRichText(product.Applications)}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - Quick Info & CTA */}
          <div className="lg:col-span-1">
            <div className="bg-gradient-to-br from-catalina-blue to-san-marino rounded-lg p-8 text-white sticky top-24">
              <h3 className="text-2xl font-bold mb-6">Get This Product</h3>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <IoCheckmark className="w-6 h-6 mr-3 flex-shrink-0 mt-1" />
                  <span>High-quality construction</span>
                </div>
                <div className="flex items-start">
                  <IoCheckmark className="w-6 h-6 mr-3 flex-shrink-0 mt-1" />
                  <span>Competitive pricing</span>
                </div>
                <div className="flex items-start">
                  <IoCheckmark className="w-6 h-6 mr-3 flex-shrink-0 mt-1" />
                  <span>Expert support</span>
                </div>
                <div className="flex items-start">
                  <IoCheckmark className="w-6 h-6 mr-3 flex-shrink-0 mt-1" />
                  <span>Fast delivery</span>
                </div>
              </div>

              <button
                onClick={handleGetQuote}
                className="w-full bg-white text-catalina-blue font-bold py-4 px-6 rounded-lg hover:bg-gray-100 transition duration-300 shadow-lg"
              >
                Request a Quote
              </button>

              <div className="mt-6 pt-6 border-t border-white/20">
                <p className="text-sm text-white/90 mb-2">Need help choosing?</p>
                <a
                  href="/contact"
                  className="text-sm font-semibold hover:underline"
                >
                  Contact our experts →
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quote Request Form Modal */}
      {showQuoteModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => setShowQuoteModal(false)}
        >
          <div 
            className="bg-white rounded-lg max-w-2xl w-full p-8 relative my-8"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setShowQuoteModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
            >
              <IoClose className="w-6 h-6" />
            </button>

            {!formSubmitted ? (
              <>
                {/* Modal Header */}
                <div className="mb-6">
                  <h3 className="text-3xl font-bold text-catalina-blue mb-2">Request a Quote</h3>
                  <p className="text-gray-600">
                    Get a personalized quote for <span className="font-semibold text-catalina-blue">{product.Title}</span>
                  </p>
                </div>

                {/* Error Message */}
                {submitStatus === 'error' && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
                    <IoClose className="w-5 h-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
                    <div>
                      <h4 className="text-red-800 font-semibold mb-1">Submission Failed</h4>
                      <p className="text-red-700 text-sm">{errorMessage}</p>
                    </div>
                  </div>
                )}

                {/* Quote Request Form */}
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Name Field */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-catalina-blue focus:border-transparent transition"
                      placeholder="John Doe"
                    />
                  </div>

                  {/* Email and Phone Row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-catalina-blue focus:border-transparent transition"
                        placeholder="john@example.com"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                        Phone Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-catalina-blue focus:border-transparent transition"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                  </div>

                  {/* Company Field */}
                  <div>
                    <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">
                      Company Name
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-catalina-blue focus:border-transparent transition"
                      placeholder="Your Company (Optional)"
                    />
                  </div>

                  {/* Message Field */}
                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                      Additional Details
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows="4"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-catalina-blue focus:border-transparent transition resize-none"
                      placeholder="Tell us about your requirements, quantity needed, delivery location, etc."
                    ></textarea>
                  </div>

                  {/* Product Info Display */}
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <p className="text-sm text-gray-600">
                      <span className="font-semibold">Product:</span> {product.Title}
                    </p>
                  </div>

                  {/* Submit Button */}
                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => setShowQuoteModal(false)}
                      disabled={isSubmitting}
                      className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex-1 px-6 py-3 bg-catalina-blue text-white font-semibold rounded-lg hover:bg-san-marino transition duration-300 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center justify-center">
                          <AiOutlineLoading3Quarters className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" />
                          Sending...
                        </span>
                      ) : (
                        'Submit Quote Request'
                      )}
                    </button>
                  </div>

                  {/* Privacy Note */}
                  <p className="text-xs text-gray-500 text-center">
                    By submitting this form, you agree to our privacy policy. We'll respond within 24 hours.
                  </p>
                </form>
              </>
            ) : (
              /* Success Message */
              <div className="text-center py-8">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <IoCheckmark className="w-10 h-10 text-green-500" />
                </div>
                <h3 className="text-2xl font-bold text-catalina-blue mb-2">Quote Request Sent!</h3>
                <p className="text-gray-600 mb-4">
                  Thank you for your interest in <span className="font-semibold">{product.Title}</span>
                </p>
                <p className="text-gray-600">
                  Our team will review your request and get back to you within 24 hours.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default ProductDetails;